import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AgentOfficeAddressDto, AgentOfficeHoursUpdateDto, OpeningHoursDto } from '@workspace/data-types';
import { AuraOpeningHoursFormComponent } from '@workspace/ng-aura';

@Component({
  selector: 'etxe-agent-office-hours-form',
  imports: [MatIconModule, MatFormFieldModule, MatInputModule, AuraOpeningHoursFormComponent],
  templateUrl: './agent-office-hours-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentOfficeHoursFormComponent {
  readonly officialAddress = input<string | null>(null);
  readonly officeAddress = input<AgentOfficeAddressDto | undefined>(undefined);
  readonly openingHours = input<OpeningHoursDto | undefined>(undefined);
  readonly useCustomAddress = input<boolean>(false);

  readonly officeAddressChange = output<AgentOfficeAddressDto>();
  readonly openingHoursChange = output<OpeningHoursDto>();
  readonly useCustomAddressChange = output<boolean>();

  readonly validityChanged = output<boolean>();
  readonly dtoChanged = output<{ dto: AgentOfficeHoursUpdateDto; changed: boolean }>();

  readonly currentUseCustom = signal<boolean>(false);
  readonly currentAddress = signal<AgentOfficeAddressDto>({});
  readonly currentHours = signal<OpeningHoursDto | undefined>(undefined);

  constructor() {
    effect(() => {
      const addr = this.officeAddress() || {};
      const hours = this.openingHours();
      const custom = this.useCustomAddress() || Boolean(addr.streetName || addr.city);

      this.currentAddress.set({ ...addr });
      this.currentHours.set(hours ? { ...hours } : undefined);
      this.currentUseCustom.set(custom);

      this.validityChanged.emit(true);
      this.dtoChanged.emit({
        dto: {
          officeAddress: custom ? addr : {},
          openingHours: hours,
        },
        changed: false,
      });
    });
  }

  toggleUseCustom(custom: boolean): void {
    this.currentUseCustom.set(custom);
    this.useCustomAddressChange.emit(custom);
    if (!custom) {
      this.currentAddress.set({});
      this.officeAddressChange.emit({});
    }
    this.emitState();
  }

  onFieldChange(field: keyof AgentOfficeAddressDto, value: string): void {
    const updated: AgentOfficeAddressDto = {
      ...this.currentAddress(),
      [field]: value.trim() || undefined,
    };
    this.currentAddress.set(updated);
    this.officeAddressChange.emit(updated);
    this.emitState();
  }

  onHoursChange(hours: OpeningHoursDto): void {
    this.currentHours.set(hours);
    this.openingHoursChange.emit(hours);
    this.emitState();
  }

  private emitState(): void {
    this.validityChanged.emit(true);
    const custom = this.currentUseCustom();
    const addr = custom ? this.currentAddress() : {};
    const hours = this.currentHours();

    this.dtoChanged.emit({
      dto: {
        officeAddress: addr,
        openingHours: hours,
      },
      changed: true,
    });
  }
}
